import { create } from "zustand";
import { dealApi, type DashboardSubsidyDealsRequest, type DealFeedRequest, type GoogleCategoryTree } from "~/api/deal";
import type { DealClientListData } from "~/types/deal";
import { apiErrorMessage, reportUnexpectedError } from "~/api/client";

interface BaseTrendingState {
  category: GoogleCategoryTree | null;
  deals: DealClientListData[];
  categories: GoogleCategoryTree[];
  page: number;
  isLoading: boolean;
  // Separate lock for fetchCategories: it must not be blocked by (or block) the deals
  // fetches below, which share `isLoading`/`trySetLoading` across several operations.
  isCategoriesLoading: boolean;
  error: string | null;
  trySetLoading: () => boolean;
  setNotLoading: () => void;
  trySetCategoriesLoading: () => boolean;
  setCategoriesNotLoading: () => void;
  fetchCategories: () => Promise<void>;
  fetchDeals: (categoryId?: string, channel?: string) => Promise<void>;
  fetchMoreDeals: (categoryId?: string, channel?: string) => Promise<void>;
}

// Budget Deals = popular + google category deals (one page each, de-duplicated)
export interface BudgetDealsState extends BaseTrendingState {
  fetchForYouDeals: (channel?: string) => Promise<void>;
  fetchMoreForYouDeals: (channel?: string) => Promise<void>;
}

export const useBudgetDealsStore = create<BudgetDealsState>((set, get) => ({
  deals: [],
  isLoading: false,
  isCategoriesLoading: false,
  error: null,
  category: null,
  categories: [],
  page: 1,

  trySetLoading: () => {
    const state = get();
    if (state.isLoading) return false;
    set({ isLoading: true, error: null });
    return true;
  },

  setNotLoading: () => set({ isLoading: false }),

  trySetCategoriesLoading: () => {
    const state = get();
    if (state.isCategoriesLoading) return false;
    set({ isCategoriesLoading: true, error: null });
    return true;
  },

  setCategoriesNotLoading: () => set({ isCategoriesLoading: false }),

  fetchCategories: async () => {
    if (!get().trySetCategoriesLoading()) return;
    try {
      const resp = await dealApi.getGoogleCategories({ count: 20 });
      const cats = resp.categories || [];
      set({ categories: cats, category: cats[0] ?? null });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch categories"),
      });
    } finally {
      get().setCategoriesNotLoading();
    }
  },

  fetchDeals: async (categoryId?: string, channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      // Reset to first page
      const currentPage = 1;
      const popularReq: DealFeedRequest = { entryPoint: "popular", page: currentPage, pageSize: 20, channel };
      const popularResp = await dealApi.getDealFeed(popularReq);

      // Determine category id to fetch
      let selectedCategoryId = categoryId || get().category?.categoryId || null;
      let fallbackCategory: GoogleCategoryTree | null = null;
      if (!selectedCategoryId) {
        const categoriesResp = await dealApi.getGoogleCategories({ count: 1 });
        const first = categoriesResp.categories?.[0] || null;
        selectedCategoryId = first?.categoryId || null;
        fallbackCategory = first;
      }

      let categoryDeals: DealClientListData[] = [];
      if (selectedCategoryId) {
        const existing = (get().categories || []).find(c => c.categoryId === selectedCategoryId) || fallbackCategory;
        const selectedCategory: GoogleCategoryTree | null = existing || { categoryId: selectedCategoryId };
        set({ category: selectedCategory });
        const gcResp = await dealApi.getDealsInGoogleCategoryTree({
          categoryId: selectedCategoryId,
          page: currentPage,
          size: 20,
        });
        categoryDeals = gcResp.deals || [];
      }

      // Merge and de-duplicate by _id
      const map = new Map<string, DealClientListData>();
      [...(popularResp.deals || []), ...categoryDeals].forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });

      set({ deals: Array.from(map.values()), page: currentPage });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch budget deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },

  fetchMoreDeals: async (categoryId?: string, channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      const newPage = (get().page || 1) + 1;

      // Determine category id to fetch
      let selectedCategoryId = categoryId || get().category?.categoryId || null;
      let fallbackCategory: GoogleCategoryTree | null = null;
      if (!selectedCategoryId) {
        const categoriesResp = await dealApi.getGoogleCategories({ count: 1 });
        const first = categoriesResp.categories?.[0] || null;
        selectedCategoryId = first?.categoryId || null;
        fallbackCategory = first;
      }

      // Fetch next pages for both sources
      const popularReq: DealFeedRequest = { entryPoint: "popular", page: newPage, pageSize: 20, channel };
      const popularResp = await dealApi.getDealFeed(popularReq);

      let categoryDeals: DealClientListData[] = [];
      if (selectedCategoryId) {
        const existing = (get().categories || []).find(c => c.categoryId === selectedCategoryId) || fallbackCategory;
        const selectedCategory: GoogleCategoryTree | null = existing || { categoryId: selectedCategoryId };
        set({ category: selectedCategory });
        const gcResp = await dealApi.getDealsInGoogleCategoryTree({
          categoryId: selectedCategoryId,
          page: newPage,
          size: 20,
        });
        categoryDeals = gcResp.deals || [];
      }

      const map = new Map<string, DealClientListData>();
      (get().deals || []).forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });
      [...(popularResp.deals || []), ...categoryDeals].forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });

      set({ deals: Array.from(map.values()), page: newPage });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch budget deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },

  fetchForYouDeals: async (channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      const currentPage = 1;
      const popularReq: DealFeedRequest = { entryPoint: "popular", page: currentPage, pageSize: 20, channel };
      const popularResp = await dealApi.getDealFeed(popularReq);
      // Merge and de-duplicate by _id
      const map = new Map<string, DealClientListData>();
      [...(popularResp.deals || [])].forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });

      set({ deals: Array.from(map.values()), page: currentPage });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch budget deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },

  fetchMoreForYouDeals: async (channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      const newPage = (get().page || 1) + 1;
      const popularReq: DealFeedRequest = { entryPoint: "popular", page: newPage, pageSize: 20, channel };
      const popularResp = await dealApi.getDealFeed(popularReq);

      const map = new Map<string, DealClientListData>();
      (get().deals || []).forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });
      [...(popularResp.deals || [])].forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });

      set({ deals: Array.from(map.values()), page: newPage });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch budget deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },
}));

// Subsidy Program = pick a subsidy category, then fetch /dashboard/subsidy_deals with googleCategoryLv1Id
export interface SubsidyDealsState extends BaseTrendingState {}

export const useSubsidyDealsStore = create<SubsidyDealsState>((set, get) => ({
  deals: [],
  isLoading: false,
  isCategoriesLoading: false,
  error: null,
  category: null,
  categories: [],
  page: 1,

  trySetLoading: () => {
    const state = get();
    if (state.isLoading) return false;
    set({ isLoading: true, error: null });
    return true;
  },

  setNotLoading: () => set({ isLoading: false }),

  trySetCategoriesLoading: () => {
    const state = get();
    if (state.isCategoriesLoading) return false;
    set({ isCategoriesLoading: true, error: null });
    return true;
  },

  setCategoriesNotLoading: () => set({ isCategoriesLoading: false }),

  fetchCategories: async () => {
    if (!get().trySetCategoriesLoading()) return;
    try {
      const categories = await dealApi.getSubsidyCategories();
      const cats = categories.categories || [];
      set({ categories: cats, category: cats[0] ?? null });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch subsidy categories"),
      });
    } finally {
      get().setCategoriesNotLoading();
    }
  },

  fetchDeals: async (categoryId?: string, channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      let selectedCategoryId = categoryId || get().category?.categoryId || null;
      if (!selectedCategoryId) {
        const categories = await dealApi.getSubsidyCategories();
        selectedCategoryId = categories.categories?.[0]?.categoryId || null;
      }

      let deals: DealClientListData[] = [];
      if (selectedCategoryId) {
        const existing = (get().categories || []).find(c => c.categoryId === selectedCategoryId) || null;
        const selectedCategory: GoogleCategoryTree | null = existing || { categoryId: selectedCategoryId };
        set({ category: selectedCategory });
        const req: DashboardSubsidyDealsRequest = { googleCategoryLv1Id: selectedCategoryId, channel };
        const resp = await dealApi.getDashboardSubsidyDeals(req);
        deals = resp.deals || [];
      }

      set({ deals, page: 1 });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch subsidy deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },

  fetchMoreDeals: async (categoryId?: string, channel?: string) => {
    if (!get().trySetLoading()) return;
    try {
      const newPage = (get().page || 1) + 1;
      let selectedCategoryId = categoryId || get().category?.categoryId || null;
      if (!selectedCategoryId) {
        const categories = await dealApi.getSubsidyCategories();
        selectedCategoryId = categories.categories?.[0]?.categoryId || null;
      }

      let moreDeals: DealClientListData[] = [];
      if (selectedCategoryId) {
        const existing = (get().categories || []).find(c => c.categoryId === selectedCategoryId) || null;
        const selectedCategory: GoogleCategoryTree | null = existing || { categoryId: selectedCategoryId };
        set({ category: selectedCategory });
        const req: DashboardSubsidyDealsRequest = { googleCategoryLv1Id: selectedCategoryId, channel };
        const resp = await dealApi.getDashboardSubsidyDeals(req);
        moreDeals = resp.deals || [];
      }

      const map = new Map<string, DealClientListData>();
      (get().deals || []).forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });
      (moreDeals || []).forEach(d => {
        if (d && d._id && !map.has(d._id)) map.set(d._id, d);
      });

      set({ deals: Array.from(map.values()), page: newPage });
    } catch (error) {
      reportUnexpectedError(error);
      set({
        error: apiErrorMessage(error, "Failed to fetch subsidy deals"),
      });
    } finally {
      get().setNotLoading();
    }
  },
}));
